import { mui } from "@/mui";
import { XIcon, MessageSquareIcon, Settings, Trash2Icon, PlusIcon, UsersIcon, UserPlusIcon, CrownIcon } from "lucide-react";
import type { ReactNode } from "react";
import { useState, useEffect } from "react";
import { useLiveQuery } from 'dexie-react-hooks';
import { useMobyDB, useMobyNodeClient, useSession } from "./provider";
import { Link, useLocation } from "wouter";
import { MessageDestinationType } from "@/moby/db";
import { getPeerAvatarColor, renderPeerAvatarEmoji } from "@/lib/peer-profile-utils";
import { PeerHealthDot } from "./peer-health-dot";
import { UserPreferencesDialog } from "./user-preferences-dialog";
import { useLayoutPreferences, SPLIT_PRESETS } from "@/hooks/useLayoutPreferences";
import { NewGroupChatDialog } from "./chat/new-group-chat-dialog";
import { InviteUsersToGroupDialog } from "./chat/invite-users-to-group-dialog";

interface ChatDrawerProps {
  children: ReactNode | ((props: { unseenCount: number }) => ReactNode);
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function ChatDrawer({ children, open: controlledOpen, onOpenChange: controlledOnOpenChange }: ChatDrawerProps) {
  const db = useMobyDB();
  const node = useMobyNodeClient();
  const session = useSession();
  const [location, navigate] = useLocation();
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const isOpen = controlledOpen !== undefined ? controlledOpen : uncontrolledOpen;
  const setIsOpen = (open: boolean) => {
    setUncontrolledOpen(open);
    controlledOnOpenChange?.(open);
  };

  const [activeTab, setActiveTab] = useState<string>("p2p");
  const [deletionTrigger, setDeletionTrigger] = useState(0);
  const [showNewGroupDialog, setShowNewGroupDialog] = useState(false);
  const [invitingToGroupAddress, setInvitingToGroupAddress] = useState<string | null>(null);
  const [deletingGroup, setDeletingGroup] = useState<{ address: string; name: string } | null>(null);
  const [isGroupDeleting, setIsGroupDeleting] = useState(false);

  // Query P2P conversations
  const conversations = useLiveQuery(async () => {
    const chats = await db.getLatestChats({
      type: MessageDestinationType.DM,
    });

    return Promise.all(
      chats.map(async (chat) => {
        const [contact, lastMessage] = await Promise.all([
          db.contacts.get({ address: chat.peers[0] }),
          chat.getLastMessage(),
        ]);

        return {
          chat,
          contact,
          lastMessage,
          connectionHealth: contact?.connection,
          profile: contact?.custom?.profile,
        };
      }),
    );
  }, [deletionTrigger]);

  // Query group chats (enhanced with organizer/member info)
  const groups = useLiveQuery(async () => {
    const chats = await db.getLatestChats({
      type: MessageDestinationType.GROUP,
    });

    return Promise.all(
      chats.map(async (chat) => ({
        chat,
        lastMessage: await chat.getLastMessage(),
        isOrganizer: !!chat.secret,
        memberCount: chat.peers.length,
      })),
    );
  }, [deletionTrigger]);

  // Listen for deletion and group events to refresh queries
  useEffect(() => {
    const handleRefresh = () => {
      setDeletionTrigger(prev => prev + 1);
    };

    window.addEventListener('moby-conversation-deleted', handleRefresh);
    window.addEventListener('moby-group-created', handleRefresh);
    window.addEventListener('moby-group-deleted', handleRefresh);
    window.addEventListener('moby-group-updated', handleRefresh);
    return () => {
      window.removeEventListener('moby-conversation-deleted', handleRefresh);
      window.removeEventListener('moby-group-created', handleRefresh);
      window.removeEventListener('moby-group-deleted', handleRefresh);
      window.removeEventListener('moby-group-updated', handleRefresh);
    };
  }, []);

  // Calculate unseen count (conversations with unread badges)
  const p2pList = conversations ?? [];
  const groupList = groups ?? [];
  const unseenCount = p2pList.filter(row => row.chat.getTotalBadgeCount() > 0).length
    + groupList.filter(row => row.chat.getTotalBadgeCount() > 0).length;

  const { isCompact, splitRatio, setSplitRatio } = useLayoutPreferences();

  const handleSelectChat = (chatAddress: string) => {
    // Auto-switch to middle split if list is expanded in compact mode
    if (isCompact && splitRatio > SPLIT_PRESETS.middle + 5) {
      setSplitRatio('middle');
    }
    // PANEL FIX 3: Clear overlay panels so ChatPanel renders in MainContentPanel
    window.dispatchEvent(new CustomEvent("email-panel-close"));
    window.dispatchEvent(new CustomEvent("av-stream-panel-close"));
    window.dispatchEvent(new CustomEvent("calendar-panel-close"));
    navigate(`/chats/${chatAddress}`);
    setIsOpen(false);
  };

  const handleDeleteConversation = async (e: React.MouseEvent, chatAddress: string, chatName: string) => {
    e.preventDefault();
    e.stopPropagation();

    const confirmed = window.confirm(
      `Delete conversation with ${chatName}?\n\nThis will remove all messages but keep the contact in your contacts list.`
    );

    if (confirmed) {
      try {
        await db.deleteConversation(chatAddress, session?.address || 'unknown');
        setDeletionTrigger(prev => prev + 1);
      } catch (error) {
        console.error(`[CHAT-DRAWER] Failed to delete conversation ${chatAddress}:`, error);
      }
    }
  };

  return (
    <>
    <mui.Drawer.Root open={isOpen} onOpenChange={setIsOpen} direction="left">
      {controlledOpen === undefined ? (
        <mui.Drawer.Trigger asChild>
          {typeof children === 'function' ? children({ unseenCount }) : children}
        </mui.Drawer.Trigger>
      ) : (
        typeof children === 'function' ? children({ unseenCount }) : children
      )}
      <mui.Drawer.Portal>
        <mui.Drawer.Overlay className="sm:bg-transparent" />
        <mui.Drawer.Content className="bg-surface-container-high text-on-surface fixed inset-y-0 left-0 z-50 flex h-full w-full flex-col border-r border-outline sm:ml-20 sm:w-96 overflow-hidden">
          {/* Pointer arrow — visible on desktop */}
          <div className="hidden sm:block absolute -left-2 top-6 w-0 h-0 border-y-[8px] border-y-transparent border-r-[8px] border-r-surface-container-high" />
          <div className="border-b-outline flex items-center justify-between border-b p-4">
            <h2 className="text-headline-sm">MobyChat™</h2>
            <div className="flex items-center gap-2">
              <div className="text-body-sm text-on-surface-variant">
                {p2pList.length + groupList.length} total
              </div>
              <button onClick={() => setIsOpen(false)} className="sm:hidden p-1 rounded-full hover:bg-on-surface-variant/8" aria-label="Close drawer">
                <XIcon size={20} />
              </button>
            </div>
          </div>

          {/* Tabbed content */}
          <mui.Tabs.Root
            value={activeTab}
            onValueChange={setActiveTab}
            className="flex min-w-0 flex-1 flex-col overflow-hidden"
          >
            <mui.Tabs.List>
              <mui.Tabs.Tab value="p2p">
                <span className="flex items-center gap-1.5">
                  P2P Chats
                  {p2pList.filter(r => r.chat.getTotalBadgeCount() > 0).length > 0 && (
                    <span className="bg-primary text-on-primary text-[10px] rounded-full min-w-[16px] h-[16px] flex items-center justify-center px-1">
                      {p2pList.filter(r => r.chat.getTotalBadgeCount() > 0).length}
                    </span>
                  )}
                </span>
              </mui.Tabs.Tab>
              <mui.Tabs.Tab value="groups">
                <span className="flex items-center gap-1.5">
                  P2P Groups
                  {groupList.filter(r => r.chat.getTotalBadgeCount() > 0).length > 0 && (
                    <span className="bg-tertiary text-on-tertiary text-[10px] rounded-full min-w-[16px] h-[16px] flex items-center justify-center px-1">
                      {groupList.filter(r => r.chat.getTotalBadgeCount() > 0).length}
                    </span>
                  )}
                </span>
              </mui.Tabs.Tab>
              <mui.Tabs.Tab value="settings">Settings</mui.Tabs.Tab>
              <mui.Tabs.Indicator />
            </mui.Tabs.List>

            {/* P2P Chats Tab */}
            <mui.Tabs.Panel value="p2p" className="flex-1 basis-0 overflow-y-auto">
              {p2pList.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center text-on-surface-variant px-4 py-12">
                  <MessageSquareIcon size={48} className="mb-4 opacity-38" />
                  <p className="text-body-lg mb-2">No conversations yet</p>
                  <p className="text-body-sm">
                    Add a contact and start chatting to see conversations here
                  </p>
                </div>
              ) : (
                p2pList.map((row) => {
                  const displayName = row.contact?.getDisplayName?.() ?? row.contact?.nickname ?? row.contact?.card?.name ?? `User-${row.chat.peers[0]?.slice(0, 8)}`;
                  const initials = row.contact?.getInitials?.() ?? displayName.slice(0, 2);
                  const badgeCount = row.chat.getTotalBadgeCount();
                  
                  return (
                    <div key={row.chat.address} className="group relative">
                      <button
                        onClick={() => handleSelectChat(row.chat.address)}
                        className="w-full text-left text-on-surface relative flex items-center p-3 transition duration-150 hover:bg-on-surface-variant/8"
                      >
                        <div className="relative mr-3 size-9 shrink-0">
                          <div
                            className="grid size-9 place-items-center overflow-hidden rounded-full text-white"
                            style={{ backgroundColor: getPeerAvatarColor(row.profile) }}
                          >
                            <div className="text-label-md">{initials}</div>
                            {renderPeerAvatarEmoji(row.profile, "sm")}
                          </div>
                          {/* Connection health indicator — P0-C: presence-aware derived state */}
                          {row.connectionHealth && (
                            <PeerHealthDot
                              peerId={row.chat.address}
                              connection={row.connectionHealth}
                              size="sm"
                            />
                          )}
                        </div>
                        <div className="mr-2 min-w-0 flex-1">
                          <h3 className="text-body-md font-medium truncate">{displayName}</h3>
                          <p className="text-body-sm text-on-surface-variant truncate">
                            {row.lastMessage?.text ?? "No messages yet"}
                          </p>
                        </div>
                        <div className="flex flex-col items-end gap-1 shrink-0">
                          {badgeCount > 0 && (
                            <div className="flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full bg-primary text-on-primary text-label-sm font-medium">
                              {badgeCount > 99 ? '99+' : badgeCount}
                            </div>
                          )}
                          <div className="text-on-surface-variant text-[11px]">
                            {row.lastMessage?.timestamp?.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) ?? ''}
                          </div>
                        </div>
                      </button>
                      {/* Delete button on hover */}
                      <button
                        onClick={(e) => handleDeleteConversation(e, row.chat.address, typeof displayName === 'string' ? displayName : 'this contact')}
                        className="absolute right-2 top-1/2 -translate-y-1/2 size-7 rounded-full bg-error text-on-error opacity-0 transition-opacity duration-150 hover:bg-error/80 group-hover:opacity-100 flex items-center justify-center"
                        title="Delete conversation"
                        aria-label="Delete conversation"
                      >
                        <Trash2Icon className="size-3.5" />
                      </button>
                    </div>
                  );
                })
              )}
            </mui.Tabs.Panel>

            {/* Group Chats Tab */}
            <mui.Tabs.Panel value="groups" className="flex-1 basis-0 overflow-y-auto">
              {/* Create Group Button */}
              <div className="border-b border-outline-variant p-3">
                <button 
                  onClick={() => setShowNewGroupDialog(true)}
                  className="w-full flex items-center gap-3 rounded-lg border border-outline-variant p-3 bg-surface-container hover:bg-on-surface-variant/8 active:bg-on-surface-variant/10 transition"
                >
                  <div className="bg-primary text-on-primary rounded-full p-1.5">
                    <PlusIcon size={16} />
                  </div>
                  <div className="flex-1 text-left">
                    <div className="text-title-sm">Create New Group</div>
                  </div>
                </button>
              </div>

              {groupList.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center text-on-surface-variant px-4 py-12">
                  <UsersIcon size={48} className="mb-4 opacity-38" />
                  <p className="text-body-lg mb-2">No group chats yet</p>
                  <p className="text-body-sm">
                    Create your first group to get started
                  </p>
                </div>
              ) : (
                groupList.map((row) => {
                  const name = row.chat.name ?? "Group";
                  const initials = name.slice(0, 2);
                  const badgeCount = row.chat.getTotalBadgeCount();

                  return (
                    <div key={row.chat.address} className="group relative">
                      <button
                        onClick={() => handleSelectChat(row.chat.address)}
                        className="w-full text-left text-on-surface relative flex items-center p-3 transition duration-150 hover:bg-on-surface-variant/8"
                      >
                        <div className="relative mr-3 size-9 shrink-0">
                          <div className="grid size-9 place-items-center overflow-hidden rounded-full bg-tertiary-container text-on-tertiary-container">
                            <div className="text-label-md">{initials}</div>
                          </div>
                          {/* Organizer badge */}
                          {row.isOrganizer && (
                            <div 
                              className="absolute -bottom-0.5 -right-0.5 bg-tertiary text-on-tertiary rounded-full p-0.5"
                              title="You are the organizer"
                            >
                              <CrownIcon size={10} />
                            </div>
                          )}
                        </div>
                        <div className="mr-2 min-w-0 flex-1">
                          <h3 className="text-body-md font-medium truncate">{name}</h3>
                          <p className="text-body-sm text-on-surface-variant truncate flex items-center gap-1">
                            <UsersIcon size={12} />
                            <span>{row.memberCount} member{row.memberCount !== 1 ? 's' : ''}</span>
                            {row.lastMessage?.text && (
                              <> &middot; {row.lastMessage.text}</>  
                            )}
                          </p>
                        </div>
                        <div className="flex flex-col items-end gap-1 shrink-0">
                          {badgeCount > 0 && (
                            <div className="flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full bg-tertiary text-on-tertiary text-label-sm font-medium">
                              {badgeCount > 99 ? '99+' : badgeCount}
                            </div>
                          )}
                          <div className="text-on-surface-variant text-[11px]">
                            {row.lastMessage?.timestamp?.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) ?? ''}
                          </div>
                        </div>
                      </button>
                      {/* Organizer actions on hover */}
                      {row.isOrganizer && (
                        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setInvitingToGroupAddress(row.chat.address);
                            }}
                            className="size-7 rounded-full bg-primary text-on-primary flex items-center justify-center hover:bg-primary/80"
                            title="Invite members"
                          >
                            <UserPlusIcon className="size-3.5" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setDeletingGroup({ address: row.chat.address, name });
                            }}
                            className="size-7 rounded-full bg-error text-on-error flex items-center justify-center hover:bg-error/80"
                            title="Delete group"
                          >
                            <Trash2Icon className="size-3.5" />
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </mui.Tabs.Panel>

            {/* Settings Tab */}
            <mui.Tabs.Panel value="settings" className="flex-1 basis-0 overflow-y-auto p-4">
              <div className="space-y-4">
                <UserPreferencesDialog>
                  <mui.Dialog.Trigger
                    className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-on-surface-variant/8 transition text-left"
                  >
                    <Settings size={20} className="text-on-surface-variant shrink-0" />
                    <div>
                      <div className="text-body-md font-medium">User Preferences</div>
                      <div className="text-body-sm text-on-surface-variant">Avatar, display name, presence</div>
                    </div>
                  </mui.Dialog.Trigger>
                </UserPreferencesDialog>

                <div className="border-t border-outline-variant pt-4">
                  <h3 className="text-title-sm text-on-surface-variant mb-3">Chat Info</h3>
                  <div className="space-y-2 text-body-sm text-on-surface-variant">
                    <div className="flex justify-between">
                      <span>P2P Conversations</span>
                      <span className="text-on-surface font-medium">{p2pList.length}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Group Chats</span>
                      <span className="text-on-surface font-medium">{groupList.length}</span>
                    </div>
                    {session && (
                      <div className="flex justify-between">
                        <span>Your Address</span>
                        <span className="text-on-surface font-mono text-[11px]">{session.address.slice(0, 16)}...</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </mui.Tabs.Panel>
          </mui.Tabs.Root>
        </mui.Drawer.Content>
      </mui.Drawer.Portal>
    </mui.Drawer.Root>

    {/* New Group Dialog */}
    {showNewGroupDialog && (
      <NewGroupChatDialog
        open={showNewGroupDialog}
        onOpenChange={(open: boolean) => {
          if (!open) setShowNewGroupDialog(false);
        }}
      >
        <div className="hidden" />
      </NewGroupChatDialog>
    )}

    {/* Invite Users Dialog */}
    {invitingToGroupAddress && (
      <InviteUsersToGroupDialog
        groupAddress={invitingToGroupAddress}
        open={invitingToGroupAddress !== null}
        onOpenChange={(open: boolean) => {
          if (!open) setInvitingToGroupAddress(null);
        }}
      >
        <div className="hidden" />
      </InviteUsersToGroupDialog>
    )}

    {/* Delete Group Confirmation Dialog */}
    <mui.Dialog.Root open={deletingGroup !== null} onOpenChange={(open) => { if (!open) setDeletingGroup(null); }}>
      <mui.Dialog.Portal>
        <mui.Dialog.Backdrop />
        <mui.Dialog.Popup className="bg-surface-container-high text-on-surface fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-xl p-6 shadow-lg">
          <mui.Dialog.Title className="text-title-lg mb-4">
            Delete Group
          </mui.Dialog.Title>
          <mui.Dialog.Description className="text-body-md text-on-surface-variant mb-6">
            Are you sure you want to permanently delete <strong>{deletingGroup?.name}</strong>? 
            This will remove the group and all associated chat history for all members. This action cannot be undone.
          </mui.Dialog.Description>
          <div className="flex justify-end gap-2">
            <mui.Button 
              onClick={() => setDeletingGroup(null)}
              disabled={isGroupDeleting}
              className="text-primary"
            >
              Cancel
            </mui.Button>
            <mui.Button 
              onClick={async () => {
                if (!deletingGroup) return;
                setIsGroupDeleting(true);
                try {
                  const userAddress = session?.address || 'unknown';
                  await db.deleteConversation(deletingGroup.address, userAddress, userAddress);
                  if (location === `/chats/${deletingGroup.address}`) {
                    navigate('/chats');
                  }
                  setDeletingGroup(null);
                  setDeletionTrigger(prev => prev + 1);
                } catch (error) {
                  console.error('Failed to delete group:', error);
                } finally {
                  setIsGroupDeleting(false);
                }
              }}
              disabled={isGroupDeleting}
              filled
              className="bg-error text-on-error hover:bg-error/90"
            >
              {isGroupDeleting ? 'Deleting...' : 'Delete'}
            </mui.Button>
          </div>
        </mui.Dialog.Popup>
      </mui.Dialog.Portal>
    </mui.Dialog.Root>
    </>
  );
}
